import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getWorkspaceListMemoryBlocksQueryKey } from '@/generated/hooks/rain/v1/workspace_scoped/memory_service';
import {
  getWorkspaceListToolSourcesQueryKey,
  getWorkspaceListToolsQueryKey,
} from '@/generated/hooks/rain/v1/workspace_scoped/tool_catalog_service';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import { EBuilderTool } from '../agents/builder';

/**
 * Which of the target agent's config queries a Builder tool invalidates once it
 * succeeds. Only WRITES appear here — a read (`list_*` / `get_*`) changes
 * nothing, so refetching after one would be pure noise.
 *
 * `identity` → the memory blocks (personality / instructions / tone & style).
 * `tools`    → the tool-sources list AND the derived flat tools list, which are
 *              two separate queries that both move when a source is added or
 *              removed, or when a single tool is toggled (enabled state lives in
 *              the sources response's `tool_enablements`).
 */
const TOOL_INVALIDATIONS: Record<string, readonly ('identity' | 'tools')[]> = {
  [EBuilderTool.SetMemoryConfig]: ['identity'],
  [EBuilderTool.AddToolSource]: ['tools'],
  [EBuilderTool.RemoveToolSource]: ['tools'],
  [EBuilderTool.EnableTool]: ['tools'],
  // Bulk enable moves the same two queries as the singular form. Omitting it
  // left the Automations switch reading OFF after Smith turned an automation
  // on from chat — enabling an automation means enabling its whole tool group,
  // so `enable_tools` is the call that actually lands for one.
  [EBuilderTool.EnableTools]: ['tools'],
  [EBuilderTool.DisableTool]: ['tools'],
  // `add_integration` only returns the connection the user picked — the tool
  // source that actually gives the agent the app's tools is created by the
  // follow-up `add_tool_source`, which is already mapped above. So the picker
  // itself changes nothing on the agent and needs no refetch.
};

/**
 * Builds the `onToolSucceeded` handler for the Builder chat: after a tool that
 * MUTATES the target agent's config finishes, refetch the queries that back the
 * Agent Preview pane so it shows the new state on the next tick.
 *
 * This exists because the config writes are fulfilled SERVER-SIDE by Rain (see
 * smith.k8s.yaml) — the client never runs them, so it gets no natural moment to
 * drop its cache the way a client-side mutation does. The `tool_result` event is
 * that moment. Mirrors `RainChat`'s `handleToolSucceeded` policy for the
 * automation editor.
 *
 * Keyed on the TARGET agent's config thread (the agent being built), not the
 * Builder's own conversation thread.
 */
export function useBuilderConfigInvalidation(
  targetAgentId: string,
  targetThreadId: string,
): (toolName: string) => void {
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);
  const queryClient = useQueryClient();

  return useCallback(
    (toolName: string): void => {
      const targets = TOOL_INVALIDATIONS[toolName];
      // Unknown or read-only tool — nothing to refetch.
      if (!targets || !targetAgentId || !targetThreadId) return;

      const keys: (readonly unknown[])[] = [];
      if (targets.includes('identity')) {
        keys.push(
          getWorkspaceListMemoryBlocksQueryKey(
            organization,
            workspace,
            targetAgentId,
            targetThreadId,
          ),
        );
      }
      if (targets.includes('tools')) {
        keys.push(
          getWorkspaceListToolSourcesQueryKey(
            organization,
            workspace,
            targetAgentId,
            targetThreadId,
          ),
          getWorkspaceListToolsQueryKey(
            organization,
            workspace,
            targetAgentId,
            targetThreadId,
          ),
        );
      }

      // Fire-and-forget: the pane re-renders when the refetch lands, and a
      // failed invalidation must never surface as a chat error.
      for (const queryKey of keys) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    [organization, queryClient, targetAgentId, targetThreadId, workspace],
  );
}
